(() => {
  'use strict';
  if (customElements.get('fivebests-ranking')) return;
  class FiveBestsRanking extends HTMLElement {
    connectedCallback() {
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const signal = this.controller.signal;
      const on = (node, event, fn) => node?.addEventListener(event, fn, { signal });
      const one = s => this.querySelector(s);
      const all = s => [...this.querySelectorAll(s)];
      const cards = all('[data-pick]');
      const search = one('[data-search]');
      const filters = one('[data-filters]');
      const table = one('[data-table]');
      const panel = one('[data-panel]');
      const selected = new Set();
      let activeGroup = '';
      const make = (tag, value, className) => {
        const node = document.createElement(tag);
        if (value !== undefined) node.textContent = value;
        if (className) node.className = className;
        return node;
      };
      const data = cards.map(card => ({
        ...card.dataset,
        card,
        checkbox: card.querySelector('[data-compare]'),
        haystack: [card.dataset.name, card.dataset.fit, card.dataset.group,
          card.dataset.spec1, card.dataset.spec2, card.dataset.spec3,
          card.querySelector('.model')?.textContent || ''].join(' ').toLowerCase()
      }));
      if (!search || !filters || !table || !panel) return;
      // Theme editor reconnects should start from the visible checkbox state.
      data.forEach(p => { if (p.checkbox?.checked) selected.add(p.key); });
      const updateFilter = () => {
        const term = search.value.trim().toLowerCase();
        let visible = 0;
        data.forEach(p => {
          p.card.hidden = Boolean((activeGroup && p.group !== activeGroup) || !p.haystack.includes(term));
          if (!p.card.hidden) visible++;
        });
        one('[data-results]').textContent = `Showing ${visible} of ${data.length} picks`;
        one('[data-empty]').hidden = visible !== 0;
        [...filters.children].forEach(b => b.setAttribute('aria-pressed', String(b.dataset.group === activeGroup)));
      };
      const reset = () => { search.value = ''; activeGroup = ''; updateFilter(); };
      filters.replaceChildren();
      ['', ...new Set(data.map(p => p.group).filter(Boolean))].forEach(group => {
        const button = make('button', group || `All ${data.length}`, 'filter');
        button.type = 'button'; button.dataset.group = group;
        on(button, 'click', () => { activeGroup = group; updateFilter(); });
        filters.append(button);
      });
      const updateComparison = () => {
        const chosen = data.filter(p => selected.has(p.key));
        one('[data-bar]').hidden = chosen.length === 0;
        one('[data-count]').textContent = `${chosen.length} of ${data.length} selected`;
        one('[data-show]').disabled = chosen.length < 2;
        one('[data-show]').textContent = chosen.length < 2 ? 'Select another' : 'Compare picks';
        if (chosen.length < 2) panel.hidden = true;
        const element = make('table');
        element.append(make('caption', 'Selected product specifications', 'screenreader'));
        const head = make('thead'); const headerRow = make('tr');
        const firstHeader = make('th', 'At a glance'); firstHeader.scope = 'col'; headerRow.append(firstHeader);
        chosen.forEach(p => { const th = make('th', p.name); th.scope = 'col'; headerRow.append(th); });
        head.append(headerRow); element.append(head);
        const body = make('tbody');
        const rows = [[this.dataset.spec1 || 'Feature 1', 'spec1'], [this.dataset.spec2 || 'Feature 2', 'spec2'], [this.dataset.spec3 || 'Feature 3', 'spec3'], ['Intended fit', 'fit']];
        rows.forEach(([label, key]) => {
          const row = make('tr'); const heading = make('th', label); heading.scope = 'row'; row.append(heading);
          chosen.forEach(p => row.append(make('td', p[key] || 'Not specified')));
          body.append(row);
        });
        element.append(body); table.replaceChildren(element);
      };
      on(search, 'input', updateFilter);
      on(one('[data-reset]'), 'click', () => { reset(); search.focus(); });
      data.forEach(p => on(p.checkbox, 'change', () => { p.checkbox.checked ? selected.add(p.key) : selected.delete(p.key); updateComparison(); }));
      on(one('[data-show]'), 'click', () => {
        if (selected.size < 2) return;
        panel.hidden = false; panel.focus({ preventScroll: true });
        panel.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
      });
      on(one('[data-clear]'), 'click', () => { selected.clear(); data.forEach(p => { p.checkbox.checked = false; }); updateComparison(); });
      const needs = one('[data-needs]');
      const answer = () => {
        const p = data.find(x => x.key === needs?.value);
        if (!p) return;
        const target = one('[data-answer]');
        const title = make('strong', `Start with ${p.name}.`);
        const why = make('p', p.why);
        const tradeoff = make('p', `Trade-off: ${p.tradeoff}`);
        const link = make('a', 'See this pick ↑');
        link.href = `#${p.card.id}`;
        on(link, 'click', reset);
        target.replaceChildren(title, why, tradeoff, link);
      };
      on(needs, 'change', answer);
      one('[data-tools]').hidden = cards.length === 0;
      all('[data-compare-control]').forEach(node => { node.hidden = false; });
      if (one('[data-quick]')) one('[data-quick]').hidden = cards.length === 0;
      updateFilter(); updateComparison(); answer();
    }
    disconnectedCallback() { this.controller?.abort(); }
  }
  customElements.define('fivebests-ranking', FiveBestsRanking);
})();
